<script setup lang="ts">
import {IonRange} from '@ionic/vue'
import {computed, onUpdated, PropType, ref, watch} from 'vue'
import type {RangeCustomEvent, RangeValue} from '@ionic/core/components'

const model = defineModel({type: [Number, Object] as PropType<RangeValue>, default: 0})

const props = defineProps({
    dualKnobs: {
        type: Boolean,
        required: false,
        default: false
    },
    min: {
        type: Number,
        required: false,
        default: 0
    },
    minValue: {
        type: Number,
        required: false,
        default: undefined
    },
    max: {
        type: Number,
        required: false,
        default: 100
    },
    maxValue: {
        type: Number,
        required: false,
        default: undefined
    },
    value: {
        type: [Number, Object] as PropType<RangeValue>,
        required: false,
        default: 0
    },
    pin: {
        type: Boolean,
        required: false,
        default: false
    },
    ticks: {
        type: Boolean,
        required: false,
        default: false
    },
    snaps: {
        type: Boolean,
        required: false,
        default: false
    },
    step: {
        type: Number,
        required: false,
        default: 1
    },
    color: {
        type: String,
        required: false,
        default: undefined
    },
    pinFormatter: {
        type: Function as PropType<(value: number) => string | number>,
        required: false,
        default: (value: number) => value
    },
    knobLabelFormatter: {
        type: Function as PropType<(value: number | {lower: number, upper: number}) => string | number | undefined>,
        required: false,
        default: (value: number) => undefined
    }
})

const component = ref()
const emit = defineEmits(['ion-change', 'ion-input', 'update:value'])

const onInput = (event: CustomEvent) => {
    emit('ion-input', event)
    updatePinPositions()
}

const onUpdate = (event: RangeCustomEvent) => {
    emit('ion-change', event)
}

watch(() => props.value, val => {
    model.value = val
}, {
    immediate: true
})

watch(() => model.value, () => {
    if (props.minValue) {
        if (typeof model.value === 'number') {
            if (model.value < props.minValue) {
                model.value = props.minValue
            }
        } else {
            if (model.value.lower < props.minValue) {
                model.value.lower = props.minValue
            }
            if (model.value.upper < props.minValue) {
                model.value.upper = props.minValue
            }
        }
    }

    if (props.maxValue) {
        if (typeof model.value === 'number') {
            if (model.value > props.maxValue) {
                model.value = props.maxValue
            }
        } else {
            if (model.value.lower > props.maxValue) {
                model.value.lower = props.maxValue
            }
            if (model.value.upper > props.maxValue) {
                model.value.upper = props.maxValue
            }
        }
    }
})

const getOffset = (element: HTMLElement | null) => {
    let offsetLeft = 0
    let offsetTop  = 0;

    while (element) {
        offsetLeft += element.offsetLeft
        offsetTop  += element.offsetTop
        element = element?.offsetParent as HTMLElement
    }

    return {offsetLeft, offsetTop}  
}

const knobLabel = computed(() => props.knobLabelFormatter(model.value as RangeValue) || '')
const knobColor = computed(() => props.color || 'primary')

// Thx ionic for being mobile friendly.
const updatePinPositions = () => {
    const componentEl = component.value.$el
    const pinEls = componentEl.shadowRoot.querySelectorAll('[part="pin"]')
    const componentElRect = componentEl.getBoundingClientRect()
    const componentOffsetLeft = getOffset(componentEl).offsetLeft
    const componentOffsetRight = componentOffsetLeft + componentElRect.width

    pinEls.forEach((pinEl: HTMLElement) => {
        const pinElRect = pinEl.getBoundingClientRect()
        const pinOffsetLeft = getOffset(pinEl).offsetLeft
        const pinOffsetRight = pinOffsetLeft + pinElRect.width

        const overflowLeft = Math.max(0, componentOffsetLeft - pinOffsetLeft)
        const overflowRight = Math.min(0, componentOffsetRight - pinOffsetRight)

        pinEl.style.setProperty('--ion-range-knob-pin-translation', `${overflowLeft || overflowRight}px`)
    })
}

onUpdated(() => {
    // Wheeeeeeeee.
    requestAnimationFrame(() => {
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    updatePinPositions()
                })
            })
        })
    })
})
</script>

<template>
<ion-range
    ref="component"
    :dual-knobs="props.dualKnobs"
    :min="props.min"
    :max="props.max"
    v-model="model"
    :pin="props.pin"
    :ticks="props.ticks"
    :snaps="props.snaps"
    :step="props.step"
    :pin-formatter="props.pinFormatter"
    v-on:ion-change="onUpdate"
    v-on:ion-input="onInput"
    :style="{'--knoblabel': `'${knobLabel}'`, '--knobcolor': `var(--ion-color-${knobColor})`, '--knobcolorrgb': `var(--ion-color-${knobColor}-rgb)`}"
    :class="{'has-knob-label': !(typeof knobLabel === 'string' && knobLabel.length === 0)}"
/>
</template>

<style lang="scss">
ion-range {
    &::part(pin) {
        display: flex;
        align-items: center;
        justify-content: center;

        background: var(--ion-color-dark);
        color: var(--ion-color-light);
        font-size: var(--custom-font-size-small);
        height: 2.5em;
        width: 6em;
        border-radius: 0.5rem;
        transform: scale(1) translateX(var(--ion-range-knob-pin-translation));

        top: -1.5rem;
        transition: transform 120ms ease, background 120ms ease;
    }

    &::part(pin)::before {
        content: none;
    }

    &::part(knob) {
        background: var(--knobcolor);
        box-shadow: 0 0 0.5rem 0 rgba(var(--knobcolorrgb), 0.5);
        // transform: scale(0.7);
        top: 0;
        width: 1.5rem;
        height: 1.5rem;
        transform: translate(calc(-50% + var(--knob-handle-size) / 2), 1rem);
    }

    &::part(bar) {
        background: var(--ion-color-grey-tint-1);
    }

    &::part(bar-active) {
        background: var(--knobcolor);
        box-shadow: 0 0 0.5rem 0 rgba(var(--knobcolorrgb), 0.5);
    }

    &.has-knob-label {
        &::part(knob) {
            width: auto;
            height: 2rem;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 0.5rem;
            transform: translate(calc(-50% + var(--knob-handle-size) / 2), 0.7rem);

            &:before {
                content: var(--knoblabel, '');
                font-size: 1em;
                line-height: 1em;
                padding-left: 0.6em;
                padding-right: 0.6em;
                white-space: nowrap;
                
                color: white;
                transform: translate(0, -0.07em);
                font-weight: bold;
                letter-spacing: 0;
            }
        }
    }
}
</style>
